import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { XMarkIcon, MagnifyingGlassIcon, FolderIcon } from '@heroicons/react/24/outline'
import IconSearchModal from './IconSearchModal'
import LocalIconsModal from './LocalIconsModal'

const BookmarkGroupEditModalEnhanced = ({
  isOpen,
  onClose,
  onSave,
  group = null,
  existingGroups = [],
  groupLayout = null // 当前分组的布局配置
}) => {
  const [showIconSearch, setShowIconSearch] = useState(false)
  const [showLocalIcons, setShowLocalIcons] = useState(false)
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      name: '',
      style: 'row',
      columns: 3,
      rows: 1,
      header: true,
      icon: '',
      description: '',
      iconsOnly: false,
      initiallyCollapsed: false,
      disableCollapse: false
    }
  })

  const watchedStyle = watch('style')
  const watchedIconsOnly = watch('iconsOnly')

  // 从图标搜索选择图标
  const handleSelectSearchIcon = (iconPath) => {
    setValue('icon', iconPath)
    setShowIconSearch(false)
  }

  // 从本地图标库选择图标
  const handleSelectLocalIcon = (iconPath) => {
    setValue('icon', iconPath)
    setShowLocalIcons(false)
  }

  // 获取图标预览URL
  const getIconPreviewUrl = (iconPath) => {
    if (!iconPath) return null
    
    // 完整URL
    if (iconPath.startsWith('http://') || iconPath.startsWith('https://')) {
      return iconPath
    }
    
    // Dashboard Icons (di-xxx)
    if (iconPath.startsWith('di-')) {
      const iconName = iconPath.replace('di-', '')
      return `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/${iconName}.png`
    }
    
    // Simple Icons (si-xxx)
    if (iconPath.startsWith('si-')) {
      const iconName = iconPath.replace('si-', '')
      return `https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/${iconName}.svg`
    }
    
    // Material Design Icons (mdi-xxx)
    if (iconPath.startsWith('mdi-')) {
      return null // MDI 图标需要特殊处理，这里先返回null
    }
    
    // 本地图标
    if (iconPath.startsWith('/') || iconPath.includes('.')) {
      return `/api/icons/preview?path=${encodeURIComponent(iconPath)}`
    }
    
    return null
  }

  // 当模态框打开或分组改变时，重置表单值
  useEffect(() => {
    if (isOpen) {
      if (group && groupLayout) {
        // 编辑现有分组，加载现有配置
        setValue('name', group)
        setValue('style', groupLayout.style || 'row')
        setValue('columns', groupLayout.columns || 3)
        setValue('rows', groupLayout.rows || 1)
        setValue('header', groupLayout.header !== false) // 默认为true
        setValue('icon', groupLayout.icon || '')
        setValue('description', groupLayout.description || '')
        setValue('iconsOnly', groupLayout.iconsOnly || false)
        setValue('initiallyCollapsed', groupLayout.initiallyCollapsed || false)
        setValue('disableCollapse', groupLayout.disableCollapse || false)
      } else {
        // 新建分组，使用默认值
        setValue('name', group || '')
        setValue('style', 'row')
        setValue('columns', 3)
        setValue('rows', 1)
        setValue('header', true)
        setValue('icon', '')
        setValue('description', '')
        setValue('iconsOnly', false)
        setValue('initiallyCollapsed', false)
        setValue('disableCollapse', false)
      }
    }
  }, [isOpen, group, groupLayout, setValue])

  const onSubmit = (data) => {
    // 检查组名是否已存在（编辑时排除当前组）
    const isDuplicate = existingGroups.some(existingGroup => 
      existingGroup !== group && existingGroup.toLowerCase() === data.name.toLowerCase()
    )
    
    if (isDuplicate) {
      alert('该分组名称已存在，请选择其他名称')
      return
    }

    // 处理数值，确保在合理范围内
    const finalColumns = Math.min(Math.max(parseInt(data.columns), 1), 8)
    const finalRows = Math.min(Math.max(parseInt(data.rows), 1), 6)

    // 构建布局配置对象
    const layoutConfig = {
      style: data.style,
      columns: finalColumns,
      header: data.header
    }

    // 只有在 column 布局时才添加 rows 配置
    if (data.style === 'column') {
      layoutConfig.rows = finalRows
    }

    // 添加可选配置
    if (data.icon && data.icon.trim()) {
      layoutConfig.icon = data.icon.trim()
    }
    
    if (data.description && data.description.trim()) {
      layoutConfig.description = data.description.trim()
    }

    if (data.iconsOnly) {
      layoutConfig.iconsOnly = true
    }

    if (data.initiallyCollapsed) {
      layoutConfig.initiallyCollapsed = true
    }

    if (data.disableCollapse) {
      layoutConfig.disableCollapse = true
    }

    onSave(data.name, layoutConfig)
    
    reset()
    onClose()
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <>
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-75" onClick={handleClose} />

        <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-gray-800 shadow-xl rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white">
              {group ? '编辑分组' : '添加分组'}
            </h3>
            <button
              onClick={handleClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* 分组名称 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                分组名称 *
              </label>
              <input
                {...register('name', {
                  required: '分组名称是必填的',
                  minLength: { value: 1, message: '分组名称不能为空' },
                  maxLength: { value: 50, message: '分组名称不能超过50个字符' }
                })}
                className="input"
                placeholder="例如：开发工具"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-400">{errors.name.message}</p>
              )}
            </div>

            {/* 分组描述 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                分组描述
              </label>
              <input
                {...register('description', {
                  maxLength: { value: 100, message: '描述不能超过100个字符' }
                })}
                className="input"
                placeholder="例如：开发和工具相关的书签"
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-400">{errors.description.message}</p>
              )}
              <p className="mt-1 text-xs text-gray-400">
                可选的分组描述信息
              </p>
            </div>

            {/* 图标配置 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                分组图标
              </label>
              <div className="flex items-start gap-2">
                <div className="flex-1">
                  <div className="flex">
                    <input
                      {...register('icon', {
                        pattern: {
                          value: /^[a-zA-Z0-9\-_:./]+$/,
                          message: '图标路径格式不正确'
                        }
                      })}
                      className="input rounded-r-none"
                      placeholder="例如：mdi-folder-outline 或 si-docker"
                    />
                    <button
                      type="button"
                      onClick={() => setShowIconSearch(true)}
                      className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center rounded-l-none rounded-r-none border-l-0 border-r-0"
                      title="搜索在线图标"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowLocalIcons(true)}
                      className="btn-secondary px-3 py-2 min-w-[44px] flex items-center justify-center rounded-l-none"
                      title="本地图标库"
                    >
                      <FolderIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {errors.icon && (
                    <p className="mt-1 text-sm text-red-400">{errors.icon.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-400">
                    支持 Dashboard Icons、Simple Icons、Material Icons、完整URL 或本地图标
                  </p>
                </div>

                {/* 图标预览 */}
                {watch('icon') && (
                  <div className="flex-shrink-0 ml-2">
                    <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center border border-gray-600">
                      {getIconPreviewUrl(watch('icon')) ? (
                        <img
                          src={getIconPreviewUrl(watch('icon'))}
                          alt="图标预览"
                          className="w-8 h-8 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none'
                            e.target.nextSibling.style.display = 'block'
                          }}
                        />
                      ) : null}
                      <span
                        className="text-xs text-gray-400 hidden"
                        style={{ display: getIconPreviewUrl(watch('icon')) ? 'none' : 'block' }}
                      >
                        无预览
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 布局样式 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                布局样式
              </label>
              <select {...register('style')} className="select">
                <option value="row">行布局 (Row)</option>
                <option value="column">列布局 (Column)</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">
                行布局：书签水平排列；列布局：书签垂直排列
              </p>
            </div>

            {/* 列数配置 */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {watchedStyle === 'row' ? '每行列数' : '列数'}
              </label>
              <select {...register('columns')} className="select">
                <option value="1">1 列</option>
                <option value="2">2 列</option>
                <option value="3">3 列</option>
                <option value="4">4 列</option>
                <option value="5">5 列</option>
                <option value="6">6 列</option>
                <option value="7">7 列</option>
                <option value="8">8 列</option>
              </select>
              <p className="mt-1 text-xs text-gray-400">
                {watchedStyle === 'row'
                  ? '每行显示的书签数量（Homepage 最大支持 8 列）'
                  : '分组的列数（Homepage 最大支持 8 列）'
                }
              </p>
            </div>

            {/* 行数配置 - 仅在列布局时显示 */}
            {watchedStyle === 'column' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  行数
                </label>
                <select {...register('rows')} className="select">
                  <option value="1">1 行</option>
                  <option value="2">2 行</option>
                  <option value="3">3 行</option>
                  <option value="4">4 行</option>
                  <option value="5">5 行</option>
                  <option value="6">6 行</option>
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  列布局中每列显示的书签行数
                </p>
              </div>
            )}

            {/* 显示标题 */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('header')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label className="ml-2 text-sm font-medium text-gray-300">
                显示分组标题
              </label>
              <p className="ml-2 text-xs text-gray-400">
                是否在页面上显示分组名称
              </p>
            </div>

            {/* 仅显示图标 */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('iconsOnly')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label className="ml-2 text-sm font-medium text-gray-300">
                仅显示图标
              </label>
              <p className="ml-2 text-xs text-gray-400">
                隐藏书签文字，只显示图标
              </p>
            </div>

            {/* 初始折叠 */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('initiallyCollapsed')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label className="ml-2 text-sm font-medium text-gray-300">
                初始折叠
              </label>
              <p className="ml-2 text-xs text-gray-400">
                页面加载时分组默认折叠
              </p>
            </div>

            {/* 禁用折叠 */}
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('disableCollapse')}
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
              />
              <label className="ml-2 text-sm font-medium text-gray-300">
                禁用折叠
              </label>
              <p className="ml-2 text-xs text-gray-400">
                禁用分组的折叠/展开功能
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary"
              >
                取消
              </button>
              <button
                type="submit"
                className="btn-primary"
              >
                {group ? '更新分组' : '创建分组'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

      {/* 图标搜索模态框 */}
      <IconSearchModal
        isOpen={showIconSearch}
        onClose={() => setShowIconSearch(false)}
        onSelectIcon={handleSelectSearchIcon}
      />

      {/* 本地图标模态框 */}
      <LocalIconsModal
        isOpen={showLocalIcons}
        onClose={() => setShowLocalIcons(false)}
        onSelectIcon={handleSelectLocalIcon}
      />
    </>
  )
}

export default BookmarkGroupEditModalEnhanced
